import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  icon?: React.ReactNode;
  accentColor?: 'sky' | 'emerald' | 'amber' | 'rose' | 'purple';
  footer?: React.ReactNode;
}
const sizeStyles = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-6xl'
};
const accentStyles = {
  sky: 'from-teal-500 to-cyan-500',
  emerald: 'from-emerald-500 to-teal-500',
  amber: 'from-amber-500 to-orange-500',
  rose: 'from-rose-500 to-pink-500',
  purple: 'from-purple-500 to-indigo-500'
};
const accentIconStyles = {
  sky: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
  emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  rose: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
};
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  icon,
  accentColor,
  footer
}: ModalProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const prevOpen = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setMounted(true);
      document.body.style.overflow = 'hidden';
      document.body.setAttribute('data-modal-open', 'true');
      const nav = document.querySelector('.bottom-nav-bar') as HTMLElement | null;
      if (nav) nav.style.display = 'none';
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else if (!isOpen && prevOpen.current) {
      setVisible(false);
      document.body.style.overflow = 'unset';
      document.body.removeAttribute('data-modal-open');
      const nav = document.querySelector('.bottom-nav-bar') as HTMLElement | null;
      if (nav) nav.style.display = '';
      closeTimerRef.current = setTimeout(() => {
        setMounted(false);
        closeTimerRef.current = null;
      }, 300);
    }
    prevOpen.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!mounted) return null;
  const panel = <div className={`relative w-[92%] mx-auto ${sizeStyles[size]}
        bg-white dark:bg-gray-900
        rounded-2xl shadow-2xl overflow-hidden
        flex flex-col
        max-h-[90vh]
        print:max-w-none print:shadow-none print:rounded-none`} style={{
    transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
    opacity: visible ? 1 : 0,
    transition: 'transform 280ms cubic-bezier(0.34,1.2,0.64,1), opacity 220ms ease'
  }} data-id="element-6404" data-mp="2kposx19c4g98">
      {accentColor && <div className={`h-1 bg-gradient-to-r ${accentStyles[accentColor]} rounded-t-2xl flex-shrink-0`} data-id="element-6405" data-mp="1evipq4vicnmm" />}

      {/* Header */}
      <div className={`relative flex items-center gap-2 px-4 pr-12 sm:px-6 sm:pr-14 py-3 sm:py-4
        border-b border-gray-100 dark:border-white/[0.07] print:hidden flex-shrink-0
        ${!accentColor ? 'rounded-t-2xl' : ''}`} data-id="element-6406" data-mp="1jw6ojcpza8re">
        {icon && <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
            ${accentColor ? accentIconStyles[accentColor] : 'bg-gray-100 dark:bg-white/[0.08] text-gray-600 dark:text-gray-300'}`} data-id="element-6407" data-mp="3poc0codci9gk">
            {icon}
          </div>}
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate flex-1" data-id="element-6408" data-mp="2ubu57ekmzyki">
          {title}
        </h2>
        {/* X always top-right, absolute so it never pushes title */}
        <button onClick={onClose} className="absolute top-2 right-2 sm:top-3 sm:right-3 p-2 text-gray-400
            hover:text-gray-600 dark:hover:text-gray-200
            hover:bg-gray-100 dark:hover:bg-white/[0.08] rounded-xl transition-all" data-id="element-6409" data-mp="34ccvar2sobav">
          <XIcon className="w-5 h-5" data-id="element-6410" data-mp="2qbs87ipujtbk" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="modal-scroll-body px-4 sm:px-6 py-4 pb-8 overflow-y-auto flex-1 min-h-0 overscroll-contain print:p-0 print:max-h-none print:overflow-visible styled-scrollbar" style={{
      WebkitOverflowScrolling: 'touch'
    }} data-id="element-6411" data-mp="2lkaad3j4e8ut">
        {children}
      </div>

      {/* Footer */}
      {footer && <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-white/[0.07]
          flex-shrink-0 bg-gray-50/60 dark:bg-white/[0.02] rounded-b-2xl" data-id="element-6412" data-mp="8cj4mtworw4t">
          {footer}
        </div>}
    </div>;
  return createPortal(<div className="fixed inset-0 z-[200] flex items-center justify-center print:static print:overflow-visible" data-id="element-6413" data-mp="3fsm7zxesog8">
      {/* Backdrop */}
      <div className="fixed inset-0 print:hidden" style={{
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: visible ? 'blur(4px)' : 'blur(0px)',
      opacity: visible ? 1 : 0,
      transition: 'opacity 250ms ease, backdrop-filter 250ms ease'
    }} onClick={onClose} data-id="element-6414" data-mp="r28vx718pzrz" />
      {/* Panel */}
      <div className="relative z-10 w-full flex items-center justify-center px-4 sm:px-0 pointer-events-none" data-id="element-6415" data-mp="32tmkjj4zopsr">
        <div className="pointer-events-auto w-full flex justify-center" data-id="element-6416" data-mp="36wj174ayybu2">
          {panel}
        </div>
      </div>
    </div>, document.body);
}